import { useMsal } from '@azure/msal-react';
import { loginScopes } from 'config/msal.config';
import { clearAPIDataCache } from 'hooks/useAPIData';
import Cookies from 'js-cookie';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import apiClient from 'services/_infra/apiClient';
import {
  createAppSessionFromMsalResult,
  getRefreshToken,
  logout as serverLogout,
} from 'services/auth/auth.service';
import { clearDisplayOptions } from 'store/slices/DisplayOptions/displayOptionsSlice';
import { setUserEmail, setUserIdentity } from 'store/slices/User/userSlice';
import {
  checkForNewDeployment,
  clearStaleSessionState,
  hasAlreadyReloadedForDeploy,
  markDeployReloadDone,
} from 'utils/sessions/deployment.utils';
import { attemptStaleSessionRecovery } from 'features/Auth/utils/sessionRecovery.utils';
import { AuthContext } from './AuthContext';

const AuthContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { instance, accounts, inProgress } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null (checking), true (authenticated), false (not authenticated)
  const [isInitialized, setIsInitialized] = useState(false);

  const setAuthenticated = useCallback((authenticated) => {
    setIsAuthenticated(authenticated);
    if (authenticated) {
      Cookies.set('isAuthenticated', 'true');
    } else {
      Cookies.remove('isAuthenticated');
    }
  }, []);

  // Centralized logout function
  const logout = useCallback(() => {
    setAuthenticated(false);
    Cookies.remove('isAuthenticated');
    dispatch(setUserEmail(''));
    dispatch(clearDisplayOptions());
    clearAPIDataCache();
  }, [setAuthenticated, dispatch]);

  // Centralized login function — tokens are httpOnly cookies set by the server
  const login = useCallback(() => {
    setAuthenticated(true);
  }, [setAuthenticated]);

  // Manual refresh function
  const refreshToken = useCallback(async () => {
    try {
      const response = await getRefreshToken();
      if (response?.data?.message !== 'Token refresh failed') {
        setAuthenticated(true);
        return true;
      }
      return null;
    } catch (error) {
      logout();
      return null;
    }
  }, [setAuthenticated, logout]);

  // Triggered by AppSSEContext after repeated SSE reconnect failures.
  // Tries a silent refresh; on confirmed auth failure, shows a toast with
  // a 2-minute grace timer before forcing the user back to the sign-in page.
  const recoverSseStaleSession = useCallback(
    () =>
      attemptStaleSessionRecovery({
        apiUrl: process.env.PUBLIC_API_URL,
        onRecovered: () => setAuthenticated(true),
        onForceLogout: () => {
          // Best-effort server-side logout — don't block redirect on its result.
          serverLogout().catch(() => {});
          clearStaleSessionState();
          window.location.href = '/';
        },
      }),
    [setAuthenticated],
  );

  // Tries to restore session with both Fusion and Microsoft methods
  useLayoutEffect(() => {
    // Checks if MSAL (Microsoft Authentication Library) is in the middle of handling auth logic
    if (inProgress !== 'none') return;

    let canceled = false; // Flag to prevent state updates if the component unmounts or effect reruns during async calls

    // Helper function that will reject a promise within a certain time limit
    const withTimeout = (promise, timeoutMs, message) => {
      return Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error(message)), timeoutMs)),
      ]);
    };

    const initAuth = async () => {
      // If we detect a new deployment, force a reload to ensure the latest code is running
      if (checkForNewDeployment() && !hasAlreadyReloadedForDeploy()) {
        markDeployReloadDone();
        clearStaleSessionState();
        window.location.reload();
        return;
      }

      const wasAuthenticated = Cookies.get('isAuthenticated') === 'true'; // Check if we had an authenticated session before

      // Tries Fusion session restore when wasAuthenticated is true
      try {
        if (wasAuthenticated) {
          const response = await withTimeout(getRefreshToken(), 10000, 'Auth init timeout');

          if (!canceled && response?.status === 200) {
            setAuthenticated(true);
            setIsInitialized(true);
            return;
          }
        }
      } catch (error) {
        console.error('Fusion session restore failed during auth initialization:', error);
      }

      // Tries Microsoft session restore
      try {
        const userMicrosoftAccount =
          instance?.getActiveAccount?.() || accounts?.[0] || instance?.getAllAccounts?.()?.[0];

        if (userMicrosoftAccount) {
          instance.setActiveAccount(userMicrosoftAccount);

          const msalResponse = await withTimeout(
            instance.acquireTokenSilent({
              ...loginScopes,
              account: userMicrosoftAccount,
            }),
            10000,
            'MSAL silent token acquisition timeout',
          );

          const sessionResponse = await withTimeout(
            createAppSessionFromMsalResult(msalResponse),
            10000,
            'MSAL session creation timeout',
          );

          if (!canceled && sessionResponse?.status === 200) {
            setAuthenticated(true);
            setIsInitialized(true);
            dispatch(
              setUserIdentity({
                username: sessionResponse.data.userName,
                email: sessionResponse.data.email,
                organization: sessionResponse.data.organization || 'undefined',
              }),
            );
            return;
          }
        }
      } catch (error) {
        console.error('Microsoft session restore failed during auth initialization:', error);
      }

      // This only triggers if both Fusion and Microsoft session restore failed
      if (!canceled) {
        if (wasAuthenticated) {
          Cookies.remove('isAuthenticated'); // Clear auth cookie if session restore failed
        }

        setAuthenticated(false);
        setIsInitialized(true);
      }
    };

    initAuth();

    return () => {
      canceled = true; // Set the flag to prevent state updates after unmount or effect rerun
    };
  }, [accounts, inProgress, instance, setAuthenticated, dispatch]);

  useLayoutEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      (response) => {
        if (response?.data?.message === 'User Logged Out Successfully') {
          logout();
        }

        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // With no session there is no token to refresh — a 401 here just means
        // "not logged in", not "expired". The public flows (activation, which
        // clears cookies before calling the API) would otherwise turn every
        // 401 into a refresh attempt and then a logout when it fails. Mirrors
        // the `wasAuthenticated` gate initAuth checks before a session restore.
        if (Cookies.get('isAuthenticated') !== 'true') {
          return Promise.reject(error);
        }

        // Don't retry if:
        // 1. Already retried this request (_retry flag)
        // 2. Server indicates it already attempted refresh
        // 3. This is the refresh endpoint itself
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !error.response?.data?.attemptedRefresh &&
          !originalRequest.url?.includes('/auth/login') &&
          !originalRequest.url?.includes('/auth/refresh')
        ) {
          originalRequest._retry = true;

          try {
            const refreshResponse = await getRefreshToken();

            if (!refreshResponse || refreshResponse.status !== 200) {
              console.error('Invalid refresh response:', refreshResponse);
              logout();
              return Promise.reject(error);
            }

            // Retry original request — server set new httpOnly cookies
            return apiClient(originalRequest);
          } catch (err) {
            console.error('Error refreshing token (getRefreshToken):', err);
            logout();
            return Promise.reject(err);
          }
        }

        // If refresh already attempted or this is a refresh endpoint failure, reject
        if (
          error.response?.status === 401 &&
          (error.response?.data?.attemptedRefresh || originalRequest.url?.includes('/auth/refresh'))
        ) {
          logout();
        }

        return Promise.reject(error);
      },
    );

    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  const value = useMemo(
    () => ({
      // accessToken is kept as a truthy/falsy signal for backward compatibility
      // with components that guard on it — actual tokens are httpOnly cookies
      accessToken: isAuthenticated ? true : null,
      isAuthenticated,
      isInitialized,
      setAccessToken: setAuthenticated,
      setIsAuthenticated,
      login,
      logout,
      refreshToken,
      recoverSseStaleSession,
    }),
    [
      isAuthenticated,
      isInitialized,
      setAuthenticated,
      login,
      logout,
      refreshToken,
      recoverSseStaleSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
